import EditableDataTable from '../components/EditableDataTable';
import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import SharePointUpload from '../components/SharePointUpload';

const DEPARTMENTS = [
  'Executive Management','Legal','Finance','Human Resources','Procurement',
  'Operations','Engineering','HSE','IT','Business Development','Commercial','Administration',
];

const KANBAN_STAGES = [
  { id:'intake',            label:'Intake',              color:'var(--muted)' },
  { id:'pre_litigation',    label:'Pre-Litigation',      color:'var(--royal)' },
  { id:'litigation',        label:'Litigation',          color:'var(--danger)' },
  { id:'arbitration',       label:'Arbitration',         color:'var(--warn)' },
  { id:'settlement_closed', label:'Settlement / Closed', color:'var(--success)' },
];

const COST_CATEGORIES = [
  { key:'external_counsel',  label:'External Counsel Fees' },
  { key:'filing_fee',        label:'Court Filing Fees' },
  { key:'expert_witness',    label:'Expert Witness Fees' },
  { key:'internal_legal',    label:'Internal Legal Hours' },
  { key:'settlement',        label:'Settlement / Award Payment' },
  { key:'miscellaneous',     label:'Miscellaneous' },
];

export default function CaseManagement({ showToast, editMode }) {
  const [cases, setCases]           = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    description: '', originating_department: '', external_solicitor: '',
    court_forum: '', next_hearing_date: '',
  });

  // Structured cost fields (using case_costs endpoint)
  const [costs, setCosts] = useState({
    external_counsel: '', filing_fee: '', expert_witness: '',
    internal_legal: '', settlement: '', miscellaneous: '',
  });

  const [eventForm, setEventForm] = useState({
    event_type: 'note', description: '', event_date: new Date().toISOString().slice(0,10),
  });
  const [addingCost, setAddingCost] = useState(false);
  const [newCostForm, setNewCostForm] = useState({ category:'external_counsel', description:'', amount:'', currency:'NGN', date_incurred: new Date().toISOString().slice(0,10) });

  useEffect(() => { loadCases(); }, []);

  async function loadCases() {
    try { const { data } = await api.getCases(); setCases(data || []); }
    catch { showToast('Failed to load cases'); }
    finally { setLoading(false); }
  }

  async function submitCase() {
    if (!form.description.trim()) { showToast('Matter description is required'); return; }
    if (!form.originating_department) { showToast('Please select the originating department'); return; }
    setSubmitting(true);
    try {
      const { data: newCase } = await api.createCase({
        description: form.description,
        originating_department: form.originating_department,
        external_solicitor: form.external_solicitor || null,
        court_forum: form.court_forum || null,
        next_hearing_date: form.next_hearing_date || null,
      });
      // Log each non-empty cost entry
      for (const cat of COST_CATEGORIES) {
        const amt = parseFloat(costs[cat.key]);
        if (amt > 0) {
          await api.addCaseCost(newCase.id, {
            category: cat.key,
            description: cat.label,
            amount: amt,
            date_incurred: new Date().toISOString().slice(0,10),
          });
        }
      }
      showToast('Case opened successfully');
      setShowForm(false);
      setForm({ description:'', originating_department:'', external_solicitor:'', court_forum:'', next_hearing_date:'' });
      setCosts({ external_counsel:'', filing_fee:'', expert_witness:'', internal_legal:'', settlement:'', miscellaneous:'' });
      loadCases();
    } catch (err) {
      showToast('Failed to open case: ' + (err.response?.data?.error || err.message));
    } finally { setSubmitting(false); }
  }

  async function moveCase(id, newStage) {
    try { await api.updateCase(id, { stage: newStage }); showToast('Case moved'); loadCases(); }
    catch { showToast('Move failed'); }
  }

  async function viewCaseDetail(id) {
    try { const { data } = await api.getCase(id); setSelectedCase(data); }
    catch { showToast('Failed to load case'); }
  }

  async function addEvent() {
    if (!selectedCase || !eventForm.description.trim()) { showToast('Enter event description'); return; }
    try {
      await api.addCaseEvent(selectedCase.id, eventForm);
      showToast('Event added');
      viewCaseDetail(selectedCase.id);
      setEventForm({ event_type:'note', description:'', event_date:new Date().toISOString().slice(0,10) });
    } catch { showToast('Failed to add event'); }
  }

  async function handleAddCost() {
    if (!selectedCase || !newCostForm.amount) { showToast('Enter an amount'); return; }
    setAddingCost(true);
    try {
      await api.addCaseCost(selectedCase.id, {
        ...newCostForm,
        amount: parseFloat(newCostForm.amount),
      });
      showToast('Cost recorded');
      setNewCostForm({ category:'external_counsel', description:'', amount:'', currency:'NGN', date_incurred: new Date().toISOString().slice(0,10) });
      viewCaseDetail(selectedCase.id);
    } catch { showToast('Failed to record cost'); }
    finally { setAddingCost(false); }
  }

  const casesByStage = (stageId) => cases.filter(c => c.stage === stageId);

  const totalEstimated = Object.values(costs).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const stageCls = { intake:'badge-gray', pre_litigation:'badge-blue', litigation:'badge-red', arbitration:'badge-gold', settlement_closed:'badge-green' };

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Case Management</div>
        <button className="topbar-btn btn-primary" onClick={() => setShowForm(!showForm)}>+ Open New Case</button>
      </div>

      {/* New Case Form */}
      {showForm && (
        <div className="card" style={{marginBottom:18}}>
          <div className="card-title">Open New Case</div>
          <div className="form-grid">
            <div className="form-group full">
              <label>Matter Description <span style={{color:'var(--danger)'}}>*</span></label>
              <textarea
                value={form.description}
                onChange={e => setForm({...form, description:e.target.value})}
                placeholder="Describe the legal matter, parties involved, and background..."
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Originating Department <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={form.originating_department} onChange={e => setForm({...form, originating_department:e.target.value})}>
                <option value="">-- Select Department --</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>External Solicitor / Firm</label>
              <input type="text" value={form.external_solicitor} onChange={e=>setForm({...form,external_solicitor:e.target.value})} placeholder="e.g. Aluko & Oyebode" />
            </div>
            <div className="form-group">
              <label>Court / Arbitration Forum</label>
              <input type="text" value={form.court_forum} onChange={e=>setForm({...form,court_forum:e.target.value})} placeholder="e.g. Federal High Court, Lagos" />
            </div>
            <div className="form-group">
              <label>Next Hearing Date</label>
              <input type="date" value={form.next_hearing_date} onChange={e=>setForm({...form,next_hearing_date:e.target.value})} />
            </div>
          </div>

          {/* Structured Cost Breakdown */}
          <div style={{marginTop:16,padding:14,background:'var(--pale)',borderRadius:8,border:'1px solid var(--border)'}}>
            <div style={{fontSize:12,fontWeight:700,color:'var(--navy)',marginBottom:12,textTransform:'uppercase',letterSpacing:'0.5px'}}>
              Cost Estimate (NGN) — Optional
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
              {COST_CATEGORIES.map(cat => (
                <div key={cat.key} className="form-group" style={{margin:0}}>
                  <label style={{fontSize:11}}>{cat.label}</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={costs[cat.key]}
                    onChange={e => setCosts({...costs, [cat.key]: e.target.value})}
                    style={{fontSize:12}}
                  />
                </div>
              ))}
            </div>
            {totalEstimated > 0 && (
              <div style={{marginTop:10,fontSize:12,fontWeight:600,color:'var(--navy)',textAlign:'right'}}>
                Total Estimated: NGN {totalEstimated.toLocaleString()}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button className="topbar-btn btn-ghost" onClick={()=>setShowForm(false)}>Cancel</button>
            <button className="topbar-btn btn-primary" onClick={submitCase} disabled={submitting}>
              {submitting ? 'Opening...' : 'Open Case'}
            </button>
          </div>
        </div>
      )}

      {/* Case Register */}
      {loading ? <div className="loading">Loading...</div> : (
        editMode ? (
          <div className="card">
            <div className="card-title">Case Register (Edit Mode)</div>
            <EditableDataTable
              columns={[
                {key:'ref',label:'Case Ref'},{key:'description',label:'Matter'},{key:'originating_department',label:'Department'},
                {key:'stage',label:'Stage'},{key:'counsel_name',label:'Counsel'},{key:'external_solicitor',label:'External Solicitor'},
                {key:'court_forum',label:'Court / Forum'},{key:'next_hearing_date',label:'Next Hearing'},
              ]}
              data={cases}
              editable={true}
              onDataChange={updated => setCases(updated)}
              renderCell={(row, colKey) => {
                if (colKey === 'ref') return <span style={{fontWeight:600,color:'var(--mid)',fontSize:11}}>{row.ref}</span>;
                if (colKey === 'description') return (row.description||'').slice(0,80) + (row.description?.length > 80 ? '...' : '');
                if (colKey === 'stage') return <span className={'badge ' + (stageCls[row.stage]||'badge-gray')}>{(row.stage||'').replace(/_/g,' ')}</span>;
                if (colKey === 'next_hearing_date') return row.next_hearing_date ? new Date(row.next_hearing_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-';
                if (colKey === 'counsel_name') return row.counsel_name || 'Unassigned';
                return row[colKey] ?? '-';
              }}
            />
          </div>
        ) : (
          <div className="kanban">
            {KANBAN_STAGES.map(stage => (
              <div className="kanban-col" key={stage.id}>
                <div className="kanban-col-title" style={{color:stage.color}}>
                  {stage.label}
                  <span className="nav-badge" style={{background: stage.id==='litigation'?'var(--danger)':stage.id==='arbitration'?'var(--gold)':'var(--border)', color:'#fff'}}>
                    {casesByStage(stage.id).length}
                  </span>
                </div>
                {casesByStage(stage.id).map(c => (
                  <div
                    className="kanban-card" key={c.id}
                    style={stage.id==='litigation'?{borderLeft:'3px solid var(--danger)'}:stage.id==='arbitration'?{borderLeft:'3px solid var(--gold)'}:stage.id==='settlement_closed'?{opacity:.75}:{}}
                    onClick={() => viewCaseDetail(c.id)}
                  >
                    <div className="kanban-card-title">{(c.description||'').slice(0,60)}{(c.description||'').length>60?'...':''}</div>
                    <div className="kanban-card-meta">{c.originating_department || 'N/A'} &middot; {new Date(c.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
                    {c.next_hearing_date && stage.id !== 'settlement_closed' && (
                      <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>
                        Hearing: {new Date(c.next_hearing_date).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}
                      </div>
                    )}
                    <div className="kanban-card-footer">
                      <span style={{fontSize:11,color:'var(--muted)'}}>{c.counsel_name || 'Unassigned'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )
      )}

      {/* Case Detail */}
      {selectedCase && (
        <div className="card" style={{marginTop:20}}>
          <div className="card-title" style={{display:'flex',alignItems:'center',gap:10}}>
            <span>{selectedCase.ref}</span>
            <span className={'badge ' + (stageCls[selectedCase.stage]||'badge-gray')}>{(selectedCase.stage||'').replace(/_/g,' ')}</span>
            <button className="modal-close" style={{marginLeft:'auto'}} onClick={()=>setSelectedCase(null)}>&times;</button>
          </div>

          <div style={{fontSize:13,color:'var(--dark)',marginBottom:14,padding:'10px 12px',background:'var(--pale)',borderRadius:8}}>
            {selectedCase.description}
          </div>

          <div className="row-2">
            {/* Left: Case Info + Costs */}
            <div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,fontSize:13,marginBottom:16}}>
                <div><span style={{color:'var(--muted)',fontSize:11}}>Department</span><br/><strong>{selectedCase.originating_department || 'N/A'}</strong></div>
                <div><span style={{color:'var(--muted)',fontSize:11}}>External Solicitor</span><br/><strong>{selectedCase.external_solicitor || 'N/A'}</strong></div>
                <div><span style={{color:'var(--muted)',fontSize:11}}>Court / Forum</span><br/><strong>{selectedCase.court_forum || 'N/A'}</strong></div>
                <div><span style={{color:'var(--muted)',fontSize:11}}>Assigned Counsel</span><br/><strong>{selectedCase.counsel_name || 'Unassigned'}</strong></div>
                {selectedCase.next_hearing_date && (
                  <div><span style={{color:'var(--muted)',fontSize:11}}>Next Hearing</span><br/>
                    <strong style={{color:'var(--danger)'}}>{new Date(selectedCase.next_hearing_date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</strong>
                  </div>
                )}
              </div>

              {/* Cost Breakdown */}
              <div style={{padding:12,background:'var(--pale)',borderRadius:8,marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:'var(--navy)',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                  Cost Register
                </div>
                {(selectedCase.costs || []).length === 0 ? (
                  <p style={{fontSize:12,color:'var(--muted)'}}>No costs recorded yet.</p>
                ) : (
                  <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                    <thead><tr style={{borderBottom:'1px solid var(--border)'}}>
                      <th style={{textAlign:'left',padding:'4px 0',color:'var(--muted)',fontWeight:600}}>Category</th>
                      <th style={{textAlign:'left',padding:'4px 0',color:'var(--muted)',fontWeight:600}}>Description</th>
                      <th style={{textAlign:'right',padding:'4px 0',color:'var(--muted)',fontWeight:600}}>Amount (NGN)</th>
                    </tr></thead>
                    <tbody>
                      {(selectedCase.costs||[]).map(c => (
                        <tr key={c.id} style={{borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'5px 0',color:'var(--mid)',fontWeight:600}}>{COST_CATEGORIES.find(x=>x.key===c.category)?.label||c.category}</td>
                          <td style={{padding:'5px 0',color:'var(--dark)'}}>{c.description}</td>
                          <td style={{padding:'5px 0',textAlign:'right',fontWeight:600}}>
                            {(c.currency==='USD' ? 'USD ' : '₦') + Number(c.amount||0).toLocaleString(undefined,{minimumFractionDigits:0})}
                          </td>
                        </tr>
                      ))}
                      <tr style={{borderTop:'2px solid var(--border)',fontWeight:700}}>
                        <td colSpan={2} style={{padding:'6px 0',color:'var(--navy)'}}>Total</td>
                        <td style={{padding:'6px 0',textAlign:'right',color:'var(--danger)'}}>
                          NGN {(selectedCase.costs||[]).reduce((s,c) => s+(Number(c.amount)||0), 0).toLocaleString('en-NG',{minimumFractionDigits:0})}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}

                {/* Add Cost */}
                <div style={{marginTop:12,paddingTop:10,borderTop:'1px solid var(--border)'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--navy)',marginBottom:8}}>Record New Cost</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                    <select value={newCostForm.category} onChange={e=>setNewCostForm({...newCostForm,category:e.target.value})} style={{fontSize:12,padding:'5px 8px'}}>
                      {COST_CATEGORIES.map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    <select value={newCostForm.currency} onChange={e=>setNewCostForm({...newCostForm,currency:e.target.value})} style={{fontSize:12,padding:'5px 8px',width:70}}>
                      <option value="NGN">NGN</option>
                      <option value="USD">USD</option>
                    </select>
                    <input type="number" placeholder="Amount" value={newCostForm.amount} onChange={e=>setNewCostForm({...newCostForm,amount:e.target.value})} style={{fontSize:12,padding:'5px 8px'}} />
                    <input type="text" placeholder="Description (optional)" value={newCostForm.description} onChange={e=>setNewCostForm({...newCostForm,description:e.target.value})} style={{fontSize:12,padding:'5px 8px'}} />
                    <input type="date" value={newCostForm.date_incurred} onChange={e=>setNewCostForm({...newCostForm,date_incurred:e.target.value})} style={{fontSize:12,padding:'5px 8px'}} />
                  </div>
                  <button className="sig-btn primary" onClick={handleAddCost} disabled={addingCost} style={{fontSize:12}}>
                    {addingCost ? 'Saving...' : 'Record Cost'}
                  </button>
                </div>
              </div>

              {/* Move stage */}
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {KANBAN_STAGES.filter(s=>s.id !== selectedCase.stage).map(s => (
                  <button key={s.id} className="sig-btn ghost" onClick={() => moveCase(selectedCase.id, s.id)}>
                    Move to {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Timeline + Add Event */}
            <div>
              <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>Case Timeline</div>
              <ul className="timeline" style={{maxHeight:300,overflowY:'auto'}}>
                {(selectedCase.events || []).map(e => (
                  <li className="tl-item" key={e.id}>
                    <div className={'tl-dot ' + (e.event_type==='hearing'?'gold':e.event_type==='ruling'?'red':'green')}></div>
                    <div className="tl-label">{e.description}</div>
                    <div className="tl-meta">{e.event_type} &middot; {new Date(e.event_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
                  </li>
                ))}
                {(!selectedCase.events || selectedCase.events.length === 0) && (
                  <li style={{fontSize:12,color:'var(--muted)'}}>No events recorded yet</li>
                )}
              </ul>
              <div style={{marginTop:12,padding:12,background:'var(--pale)',borderRadius:8}}>
                <div style={{fontSize:12,fontWeight:600,marginBottom:8}}>Add Event</div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <select value={eventForm.event_type} onChange={e=>setEventForm({...eventForm,event_type:e.target.value})} style={{fontSize:12,padding:'4px 8px'}}>
                    <option value="note">Note</option>
                    <option value="filing">Filing</option>
                    <option value="hearing">Hearing</option>
                    <option value="ruling">Ruling</option>
                    <option value="settlement">Settlement</option>
                  </select>
                  <input type="date" value={eventForm.event_date} onChange={e=>setEventForm({...eventForm,event_date:e.target.value})} style={{fontSize:12,padding:'4px 8px'}} />
                  <input type="text" value={eventForm.description} onChange={e=>setEventForm({...eventForm,description:e.target.value})} placeholder="Event description" style={{flex:1,fontSize:12,padding:'4px 8px'}} />
                  <button className="sig-btn primary" onClick={addEvent}>Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <SharePointUpload linkedEntityType="case" linkedEntityId={selectedCase?.id} showToast={showToast} />
    </div>
  );
}
